import { useCallback, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { userJourneyReducer } from './userJourneyReducer'
import {
  JourneyOutcome,
  JourneyTerminationReason,
  UserJourney,
  UserJourneyAction,
  UserJourneyContextProps,
  UserJourneyListener,
  UserJourneyState,
} from './types'

const initialState: UserJourneyState = {
  history: [],
  active: undefined,
  journeys: {},
}

/**
 * Holds the client-side journey state machine. Ported from cash-web
 * `libs/shared/user-journey/feature-main`, with the redux-inspect reducer wrapper
 * replaced by a local dispatch that notifies subscribers synchronously — reporting
 * sinks subscribe to observe completions before state (and the page) can go away.
 */
export const useUserJourney = (): UserJourneyContextProps => {
  const [state, setState] = useState<UserJourneyState>(initialState)
  const stateRef = useRef<UserJourneyState>(initialState)
  const listenersRef = useRef<Set<UserJourneyListener>>(new Set())

  const dispatch = useCallback((action: UserJourneyAction): void => {
    const prevState = stateRef.current
    const nextState = userJourneyReducer(prevState, action)
    stateRef.current = nextState
    setState(nextState)
    listenersRef.current.forEach((listener) => {
      try {
        listener({ action, prevState, state: nextState })
      } catch {
        // A faulty listener must not block other listeners or fail the dispatching caller
      }
    })
  }, [])

  const subscribe = useCallback((listener: UserJourneyListener): (() => void) => {
    listenersRef.current.add(listener)
    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const activeJourney = useMemo(() => (state.active !== undefined ? state.journeys[state.active] : undefined), [state.active, state.journeys])
  const historyJourneys = useMemo(() => state.history.map((name) => state.journeys[name]).filter(Boolean), [state.history, state.journeys])

  const startJourney = useCallback(
    (name: string): void => {
      dispatch({
        type: 'USER_JOURNEY_START',
        id: uuidv4(),
        name,
      })
    },
    [dispatch]
  )
  const resumeJourney = useCallback(
    (journey: UserJourney, esid: string): void => {
      dispatch({
        type: 'USER_JOURNEY_RESUME',
        name: journey.name,
        journey,
        esid,
      })
    },
    [dispatch]
  )
  const completeJourney = useCallback(
    (name: string, outcome: JourneyOutcome, terminationReason?: JourneyTerminationReason): void => {
      dispatch({
        type: 'USER_JOURNEY_COMPLETE',
        name,
        outcome,
        terminationReason,
      })
    },
    [dispatch]
  )
  const addFrustration = useCallback(
    (name: string, frustration: string): void => {
      dispatch({
        type: 'USER_JOURNEY_ADD_FRUSTRATION',
        name,
        frustration,
      })
    },
    [dispatch]
  )
  const addFriction = useCallback(
    (name: string, friction: string): void => {
      dispatch({
        type: 'USER_JOURNEY_ADD_FRICTION',
        name,
        friction,
      })
    },
    [dispatch]
  )
  const addTag = useCallback(
    (name: string, tag: string): void => {
      dispatch({
        type: 'USER_JOURNEY_ADD_TAG',
        name,
        tag,
      })
    },
    [dispatch]
  )
  const setVariant = useCallback(
    (name: string, variant: string): void => {
      dispatch({
        type: 'USER_JOURNEY_SET_VARIANT',
        name,
        variant,
      })
    },
    [dispatch]
  )

  return useMemo(
    () => ({
      enabled: true,
      startJourney,
      resumeJourney,
      completeJourney,
      addFrustration,
      addFriction,
      addTag,
      setVariant,
      subscribe,
      active: activeJourney,
      history: historyJourneys,
    }),
    [startJourney, resumeJourney, completeJourney, addFrustration, addFriction, addTag, setVariant, subscribe, activeJourney, historyJourneys]
  )
}
